import React, { useState, useEffect } from "react";
import { migratorySentiment as texts_es } from "../../../utils/text/graphTexts";
import { migratorySentiment as texts_en } from "../../../utils/text/graphTexts_en";
import { getMigratorySentimentData } from "../../../services/dashboard";
import {
  getMonthlyMigratorySentiment,
  getYearlySentiment,
} from "../../../services/singleMonths";
import { getPreviousMonth } from "../../../utils/functions/getPreviousMonth";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from ".././../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";

import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { getMonthText } from "../../../utils/functions/getMonthText";
import { getSentimentLineGraphOptions } from "../../../utils/constants/graphConfigs";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function MigratorySentiment({ selectedCountry }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("mig-sentiment");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Unique to Tweets component
  // Never send unique states to Card components
  const [lastMonthDataNeg, setLastMonthDataNeg] = useState();
  const [lastMonthPosChange, setLastMonthPosChange] = useState();
  const [lastMonthXenoChange, setLastMonthXenoChange] = useState();

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
    }
  }, [dateOptions]);

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);

        const response = await getMigratorySentimentData(
          currentCountry,
          selectedYear
        );

        if (response) {
          const monthsRealNames = response.data.dates.map((date) =>
            getMonthText(date, language, false)
          );

          const options = getSentimentLineGraphOptions(
            response,
            monthsRealNames,
            language
          );

          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setIsDataAvailable(false);
        setOptions(null);
      }
    }

    if (selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry, selectedYear, isLoadingInitialData, currentCountry]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      let promises, callback;
      if (isMostRecentYear) {
        const previousMonth = getPreviousMonth(
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month,
          dateOptions.yearOptions
        );

        const lastMonthDataResponse = getMonthlyMigratorySentiment(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        const previousMonthDataResponse = getMonthlyMigratorySentiment(
          currentCountry,
          previousMonth.year,
          previousMonth.month
        );

        promises = [lastMonthDataResponse, previousMonthDataResponse];
        callback = (results) => {
          if (results[0].data.positive_percentage === 0) setLastMonthData("0");
          else setLastMonthData(results[0].data.positive_percentage.toFixed(1));

          if (results[0].data.xeno_percentage === 0) setLastMonthDataNeg("0");
          else setLastMonthDataNeg(results[0].data.xeno_percentage.toFixed(1));

          const posChange = (
            results[0].data.positive_percentage.toFixed(1) -
            results[1].data.positive_percentage.toFixed(1)
          ).toFixed(1);

          const negChange = (
            results[0].data.xeno_percentage.toFixed(1) -
            results[1].data.xeno_percentage.toFixed(1)
          ).toFixed(1);

          setLastMonthPosChange(posChange);
          setLastMonthXenoChange(negChange);
        };
      } else {
        const currentYearPromise = getYearlySentiment(
          currentCountry,
          selectedYear
        );

        promises = [currentYearPromise];
        callback = (results) => {
          const positiveAvgCurrentYear = results[0].data.positive_percentage;
          const xenoAvgCurrentYear = results[0].data.xeno_percentage;

          setLastMonthData(positiveAvgCurrentYear.toFixed(1));
          setLastMonthDataNeg(xenoAvgCurrentYear.toFixed(1));
        };
      }

      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setLastMonthPosChange("--");
          setLastMonthXenoChange("--");
          setLastMonthData("--");
          setLastMonthDataNeg("--");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function MigSentimentContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData ? lastMonthData + "%" : "--"}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            lastMonthData,
            lastMonthDataNeg,
            lastMonthPosChange,
            lastMonthXenoChange
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "SentimientoMigratorio" : "MigratorySentiment",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={""}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <MigSentimentContent />}
    </CardContainer>
  );
}
